import { HighScore, HighScoreScope, HighScoreSet } from '../types/game';

const BASE_HIGHSCORE_KEY = 'yatzy-highscores';
const STORAGE_VERSION = 2;
export const MAX_LOCAL_HIGHSCORES = 25;
export const MAX_GLOBAL_HIGHSCORES = 100;

interface StoredHighScorePayload {
  version: number;
  local: HighScore[];
  global: HighScore[];
  updatedAt: string;
}

interface GlobalHighScoreResponse {
  scores: HighScore[];
  updatedAt?: string;
}

const keyForMode = (modeId?: string) => `${BASE_HIGHSCORE_KEY}:${modeId || 'classic'}`;
const isReverseMode = (modeId?: string) => (modeId || 'classic') === 'reverse';
const globalHighScorePath = (modeId?: string) => `/api/highscores/${encodeURIComponent(modeId || 'classic')}`;

const createEmptyPayload = (): StoredHighScorePayload => ({
  version: STORAGE_VERSION,
  local: [],
  global: [],
  updatedAt: new Date(0).toISOString()
});

const generateId = () => `${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;

const sanitizeName = (name: string) => {
  const trimmed = (name ?? '').toString().trim();
  if (!trimmed) return 'Anonym';
  return trimmed.slice(0, 40);
};

const normalizeEntry = (entry: any): HighScore | null => {
  if (!entry || typeof entry !== 'object') return null;
  const rawScore = Number((entry as HighScore).score);
  if (!Number.isFinite(rawScore)) return null;
  const id = typeof (entry as HighScore).id === 'string' && (entry as HighScore).id.trim().length > 0
    ? (entry as HighScore).id.trim()
    : generateId();
  const playerName = sanitizeName((entry as HighScore).playerName || '');
  const date = typeof (entry as HighScore).date === 'string' && (entry as HighScore).date.trim().length > 0
    ? (entry as HighScore).date
    : new Date().toLocaleDateString('sv-SE');
  return {
    id,
    playerName,
    score: Math.round(rawScore),
    date
  };
};

const sortAndTrim = (scores: HighScore[], limit: number, modeId?: string) =>
  scores
    .slice()
    .sort((a, b) => {
      if (isReverseMode(modeId)) {
        if (a.score !== b.score) return a.score - b.score;
      } else {
        if (b.score !== a.score) return b.score - a.score;
      }
      return a.date.localeCompare(b.date, 'sv');
    })
    .slice(0, limit);

const sanitizeList = (list: unknown, limit: number, modeId?: string) => {
  if (!Array.isArray(list)) return [] as HighScore[];
  const unique = new Map<string, HighScore>();
  list.forEach(item => {
    const normalized = normalizeEntry(item);
    if (normalized && !unique.has(normalized.id)) {
      unique.set(normalized.id, normalized);
    }
  });
  return sortAndTrim(Array.from(unique.values()), limit, modeId);
};

const migrateLegacyPayload = (legacy: unknown, modeId?: string): StoredHighScorePayload => {
  const payload = createEmptyPayload();
  payload.local = sanitizeList(legacy, MAX_LOCAL_HIGHSCORES, modeId);
  payload.updatedAt = new Date().toISOString();
  return payload;
};

const persistPayload = (modeId: string | undefined, payload: StoredHighScorePayload) => {
  try {
    const toSave: StoredHighScorePayload = {
      version: STORAGE_VERSION,
      local: payload.local,
      global: payload.global,
      updatedAt: payload.updatedAt || new Date().toISOString()
    };
    localStorage.setItem(keyForMode(modeId), JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving high score:', error);
  }
};

const readPayload = (modeId?: string): StoredHighScorePayload => {
  const key = keyForMode(modeId);
  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return createEmptyPayload();
    }

    const parsed = JSON.parse(stored);

    if (Array.isArray(parsed)) {
      const migrated = migrateLegacyPayload(parsed, modeId);
      persistPayload(modeId, migrated);
      return migrated;
    }

    if (parsed && typeof parsed === 'object') {
      const payload: StoredHighScorePayload = {
        version: STORAGE_VERSION,
        local: sanitizeList((parsed as StoredHighScorePayload).local, MAX_LOCAL_HIGHSCORES, modeId),
        global: sanitizeList((parsed as StoredHighScorePayload).global, MAX_GLOBAL_HIGHSCORES, modeId),
        updatedAt: typeof (parsed as StoredHighScorePayload).updatedAt === 'string'
          ? (parsed as StoredHighScorePayload).updatedAt
          : new Date().toISOString()
      };
      return payload;
    }
  } catch (error) {
    console.error('Error loading high scores:', error);
  }

  return createEmptyPayload();
};

const cloneSet = (payload: StoredHighScorePayload): HighScoreSet => ({
  local: payload.local.slice(),
  global: payload.global.slice(),
  updatedAt: payload.updatedAt
});

const resolveServerBaseUrl = () => {
  const envUrl = (process.env.REACT_APP_SERVER_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3081';
    }
    return window.location.origin.replace(/\/$/, '');
  }

  return 'http://localhost:3081';
};

const mergeGlobalScoresIntoPayload = (modeId: string | undefined, scores: HighScore[], updatedAt?: string) => {
  const payload = readPayload(modeId);
  payload.global = sanitizeList(scores, MAX_GLOBAL_HIGHSCORES, modeId);
  payload.updatedAt = updatedAt || new Date().toISOString();
  persistPayload(modeId, payload);
  return cloneSet(payload);
};

const ensureScopesArray = (scope?: HighScoreScope | HighScoreScope[]) => {
  if (!scope) return ['local'] as HighScoreScope[];
  return Array.isArray(scope) ? scope : [scope];
};

export const getHighScores = (modeId?: string, scope: HighScoreScope = 'local'): HighScore[] => {
  const payload = readPayload(modeId);
  return scope === 'global' ? payload.global.slice() : payload.local.slice();
};

export const getHighScoreSet = (modeId?: string): HighScoreSet => {
  const payload = readPayload(modeId);
  return cloneSet(payload);
};

export const qualifiesForHighScore = (
  score: number,
  scores: HighScore[],
  modeId?: string,
  scope: HighScoreScope = 'local'
): boolean => {
  const limit = scope === 'global' ? MAX_GLOBAL_HIGHSCORES : MAX_LOCAL_HIGHSCORES;
  const list = sanitizeList(scores, limit, modeId);

  if (list.length < limit) return true;
  if (list.length === 0) return true;
  if (isReverseMode(modeId)) {
    const highestScore = list[list.length - 1]?.score ?? Infinity;
    return score < highestScore;
  }
  const lowestScore = list[list.length - 1]?.score ?? -Infinity;
  return score > lowestScore;
};

export const addHighScore = (
  playerName: string,
  score: number,
  modeId?: string,
  scopes: HighScoreScope | HighScoreScope[] = 'local'
): HighScoreSet => {
  const payload = readPayload(modeId);
  const entry: HighScore = {
    id: generateId(),
    playerName: sanitizeName(playerName),
    score: Math.round(score),
    date: new Date().toLocaleDateString('sv-SE')
  };

  const targetScopes = ensureScopesArray(scopes);
  targetScopes.forEach(scope => {
    if (scope === 'global') {
      payload.global = sortAndTrim([...payload.global, entry], MAX_GLOBAL_HIGHSCORES, modeId);
    } else {
      payload.local = sortAndTrim([...payload.local, entry], MAX_LOCAL_HIGHSCORES, modeId);
    }
  });

  payload.updatedAt = new Date().toISOString();
  persistPayload(modeId, payload);
  return cloneSet(payload);
};

export const isHighScore = (
  score: number,
  modeId?: string,
  scope: HighScoreScope = 'local'
): boolean => {
  const payload = readPayload(modeId);
  const list = scope === 'global' ? payload.global : payload.local;
  return qualifiesForHighScore(score, list, modeId, scope);
};

export const syncGlobalHighScores = async (modeId?: string): Promise<HighScoreSet> => {
  const cached = getHighScoreSet(modeId);

  if (typeof fetch !== 'function') {
    return cached;
  }

  try {
    const response = await fetch(`${resolveServerBaseUrl()}${globalHighScorePath(modeId)}`);
    if (!response || typeof response.ok !== 'boolean' || typeof response.json !== 'function') {
      return cached;
    }
    if (!response.ok) {
      throw new Error(`Global highscore sync failed: ${response.status}`);
    }

    const payload = await response.json() as GlobalHighScoreResponse;
    return mergeGlobalScoresIntoPayload(modeId, payload.scores || [], payload.updatedAt);
  } catch (error) {
    console.warn('Could not sync global high scores, using cached values.', error);
    return cached;
  }
};

export const submitGlobalHighScore = async (
  playerName: string,
  score: number,
  modeId?: string
): Promise<HighScoreSet> => {
  if (typeof fetch !== 'function') {
    return getHighScoreSet(modeId);
  }

  try {
    const response = await fetch(`${resolveServerBaseUrl()}${globalHighScorePath(modeId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        playerName: sanitizeName(playerName),
        score: Math.round(score)
      })
    });

    if (!response || typeof response.ok !== 'boolean' || typeof response.json !== 'function') {
      return getHighScoreSet(modeId);
    }

    if (!response.ok) {
      throw new Error(`Global highscore submit failed: ${response.status}`);
    }

    const payload = await response.json() as GlobalHighScoreResponse;
    return mergeGlobalScoresIntoPayload(modeId, payload.scores || [], payload.updatedAt);
  } catch (error) {
    console.error('Error saving global high score:', error);
    return getHighScoreSet(modeId);
  }
};

export const clearHighScores = (modeId?: string, scope?: HighScoreScope): void => {
  const payload = readPayload(modeId);

  if (!scope) {
    payload.local = [];
    payload.global = [];
  } else if (scope === 'global') {
    payload.global = [];
  } else {
    payload.local = [];
  }

  payload.updatedAt = new Date().toISOString();
  persistPayload(modeId, payload);
};
